import { useSyncExternalStore } from "react";

export type SiteLanguage = "pt" | "en" | "es";

const STORAGE_KEY = "tnp.site.language";
const PROMPT_SEEN_STORAGE_KEY = "tnp.site.language.prompt.seen";
const DEFAULT_LANGUAGE: SiteLanguage = "pt";
const SUPPORTED_LANGUAGES: SiteLanguage[] = ["pt", "en", "es"];

function isBrowser() {
  return typeof window !== "undefined";
}

function normalizeLanguage(value: unknown): SiteLanguage | null {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(normalized as SiteLanguage)) {
    return normalized as SiteLanguage;
  }
  return null;
}

function resolveInitialLanguage(): SiteLanguage {
  if (!isBrowser()) return DEFAULT_LANGUAGE;
  const persisted = normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
  if (persisted) return persisted;
  return DEFAULT_LANGUAGE;
}

let currentLanguage: SiteLanguage = resolveInitialLanguage();
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function syncDocumentLanguage(language: SiteLanguage) {
  if (!isBrowser()) return;
  const htmlLang =
    language === "pt"
      ? "pt-BR"
      : language === "es"
        ? "es"
        : "en";
  document.documentElement.setAttribute("lang", htmlLang);
}

syncDocumentLanguage(currentLanguage);

export function getSiteLanguage(): SiteLanguage {
  return currentLanguage;
}

export function hasUserSelectedSiteLanguage() {
  if (!isBrowser()) return false;
  return Boolean(normalizeLanguage(window.localStorage.getItem(STORAGE_KEY)));
}

export function hasSeenSiteLanguagePrompt() {
  if (!isBrowser()) return false;
  return window.localStorage.getItem(PROMPT_SEEN_STORAGE_KEY) === "1";
}

export function markSiteLanguagePromptSeen() {
  if (!isBrowser()) return;
  window.localStorage.setItem(PROMPT_SEEN_STORAGE_KEY, "1");
}

export function setSiteLanguage(language: SiteLanguage) {
  const normalized = normalizeLanguage(language);
  if (!normalized) return;
  const changed = normalized !== currentLanguage;
  currentLanguage = normalized;
  if (isBrowser()) {
    window.localStorage.setItem(STORAGE_KEY, normalized);
  }
  syncDocumentLanguage(normalized);
  if (changed) emitChange();
}

export function subscribeSiteLanguage(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSiteLanguage() {
  const language = useSyncExternalStore(
    subscribeSiteLanguage,
    getSiteLanguage,
    getSiteLanguage,
  );
  return {
    language,
    setLanguage: setSiteLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
}
